import { useCallback } from 'react';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { differenceWith } from 'lodash';
import type {
  GetProjectsForWorkspacesListQuery,
  ProjectConnection,
  Workspace,
} from '@frameio/graphql-client/generated';
import { INVITED_TO_ID } from '@frameio/workspaces/constants';
import TEXT from './generated/TEXT/useGroupProjectsByWorkspaces';

TEXT.DEFINE({
  /** Section title used for the synthetic workspace grouping projects the user is invited to */
  invitedTo: 'Invited to',
});

type GroupProjectsByWorkspacesProps = {
  data: GetProjectsForWorkspacesListQuery | undefined;
};

export const useGroupProjectsByWorkspaces = () => {
  const { invitedTo } = TEXT;

  return useCallback(
    ({ data }: GroupProjectsByWorkspacesProps) => {
      if (!data) return [];

      const invitedProjects = [
        ...(data?.account?.fullAccessProjects?.nodes || []),
        ...(data?.account?.partialAccessProjects?.nodes || []),
      ];

      const projectsList = differenceWith(
        data?.account?.projects?.nodes || [],
        invitedProjects,
        (project, invitedProject) => project.id === invitedProject.id
      );

      const projectsGroupedByWorkspaces = projectsList.reduce<Workspace[]>(
        (accumulatedGroupedProjects, project) => {
          const workspaceIndex = accumulatedGroupedProjects.findIndex(
            (workspace: Workspace) => workspace.id === project?.workspace?.id
          );

          if (workspaceIndex === -1) {
            accumulatedGroupedProjects.push({
              ...project.workspace,
              projects: {
                nodes: [project],
              },
            } as Workspace);
          } else {
            // eslint-disable-next-line no-param-reassign
            accumulatedGroupedProjects[workspaceIndex] = {
              ...accumulatedGroupedProjects[workspaceIndex],
              projects: {
                nodes: [
                  ...(accumulatedGroupedProjects[workspaceIndex]?.projects
                    ?.nodes || []),
                  project,
                ],
              } as Maybe<ProjectConnection>,
            };
          }
          return accumulatedGroupedProjects;
        },
        []
      );

      if (invitedProjects.length) {
        const invitedProjectsSection = {
          id: INVITED_TO_ID,
          __typename: 'Workspace',
          name: invitedTo,
          projectCount: invitedProjects.length,
          projects: {
            pageInfo: {},
            totalCount: invitedProjects.length,
            nodes: invitedProjects,
          },
        } as Workspace;
        projectsGroupedByWorkspaces.push(invitedProjectsSection);
      }

      return projectsGroupedByWorkspaces;
    },
    [invitedTo]
  );
};
